/**
 * Answer matcher for Categories.
 *
 * The player's answer is matched against a canonical "What is X?" / "Who is Y?"
 * answer plus an optional list of acceptable alternates. Matching is:
 *  - case-insensitive
 *  - whitespace-collapsing
 *  - tolerant of the Jeopardy question-prefix ("what is", "who is", "where is",
 *    "what are", "who are", "where are", "what was", "who was", "where was")
 *  - tolerant of articles (a, an, the) at either side
 *  - tolerant of trailing punctuation (`?`, `.`, `!`)
 *
 * We do NOT do fuzzy matching (no Levenshtein, no token bag-of-words). The
 * goal is "did you get the right answer" not "did you type it spelled
 * exactly perfectly." Misspellings will fail. Tablets with voice input give
 * accurate transcription in practice; for typed input the user can correct
 * before submitting.
 */

const PREFIX_RE =
  /^(what|who|where|when)\s+(is|are|was|were)\s+/i;
const ARTICLE_LEADING_RE = /^(a|an|the)\s+/i;
const PUNCT_TRAILING_RE = /[?.!,]+$/;

/** Normalize a string for matching. */
export function normalizeAnswer(input: string): string {
  let s = input.trim();
  // Strip surrounding quotes if any (single or double).
  s = s.replace(/^["'`“‘]+/, '').replace(/["'`”’]+$/, '');
  // Lowercase.
  s = s.toLowerCase();
  // Strip trailing punctuation.
  s = s.replace(PUNCT_TRAILING_RE, '');
  // Strip Jeopardy prefix.
  s = s.replace(PREFIX_RE, '');
  // Strip leading article.
  s = s.replace(ARTICLE_LEADING_RE, '');
  // Collapse interior whitespace.
  s = s.replace(/\s+/g, ' ').trim();
  // Strip trailing punctuation again (it may now be exposed).
  s = s.replace(PUNCT_TRAILING_RE, '');
  return s;
}

/**
 * True when `input` matches the canonical answer or any acceptable alternate.
 * Both sides are normalized before comparison.
 */
export function matchesAnswer(
  input: string,
  canonical: string,
  acceptable: readonly string[] = [],
): boolean {
  const normInput = normalizeAnswer(input);
  if (!normInput) return false;
  const targets = [canonical, ...acceptable].map(normalizeAnswer).filter(Boolean);
  return targets.includes(normInput);
}
